import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DataService } from './data.service';
import { DashboardCacheService } from './dashboard-cache.service';
import { financialYearFor } from '../views/pages/finalmimasdashboard/finalmimasdashboard.constants';

@Injectable({
  providedIn: 'root'
})
export class MimasDashboardService {
  private readonly basePath = '/api/mimas/dashboard';

  constructor(private dataService: DataService, private cache: DashboardCacheService) {}

  /** Serves the response through the stale-while-revalidate cache, keyed by API name and params */
  private cached(route: string, params: any, request: () => Observable<any>): Observable<any> {
    return this.cache.swr(route.replace(`${this.basePath}/`, ''), params, request);
  }

  /**
   * 1. GET /api/mimas/dashboard/summary-kpis
   * Top summary KPI strip metrics (Revenue, Major/Minor, Permits, Leases, Compliance, Enforcement, etc.)
   */
  getSummaryKpis(financialYear?: string): Observable<any> {
    const route = `${this.basePath}/summary-kpis`;
    if (financialYear) {
      return this.cached(route, { financialYear }, () => this.dataService.getRecordWithParams(route, { financialYear }));
    }
    return this.cached(route, {}, () => this.dataService.getRecord(route));
  }

  /**
   * POST /api/mimas/dashboard/revenue-analytics
   * Request Payload: { financialYear: "2025-2026", districtid: 0 }
   */
  getRevenueAnalytics(financialYear?: string, districtId?: string | number): Observable<any> {
    const route = `${this.basePath}/revenue-analytics`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  /**
   * 3. POST /api/mimas/dashboard/production-permits
   * Monthly production volume (Lakh MT) and e-Permit generation trends
   * Request Payload: { financialYear: "2025-2026", districtid: 0 }
   */
  getProductionPermits(financialYear?: string, districtId?: string | number): Observable<any> {
    const route = `${this.basePath}/production-permits`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  /**
   * 4. POST /api/mimas/dashboard/major-minerals-breakdown
   * Major mineral performance (Limestone, Iron Ore, Bauxite, Magnesite)
   */
  getMajorMineralsBreakdown(financialYear?: string, districtId?: string | number): Observable<any> {
    const route = `${this.basePath}/major-minerals-breakdown`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  /**
   * POST /api/mimas/dashboard/mineral-wise-revenue-realization
   * Mineral-Wise Revenue Realization (Consolidated Major & Minor mineral revenue streams)
   */
  getMineralWiseRevenueRealization(financialYear?: string, districtId?: string | number): Observable<any> {
    const route = `${this.basePath}/mineral-wise-revenue-realization`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  getMineralsBreakdown(financialYear?: string, districtId?: string | number): Observable<any> {
    return this.getMineralWiseRevenueRealization(financialYear, districtId);
  }

  /**
   * POST /api/mimas/dashboard/minor-minerals-breakdown
   * Minor mineral performance (Rough Stone, Gravel, Sand, Granite, Earth)
   */
  getMinorMineralsBreakdown(financialYear?: string, districtId?: string | number): Observable<any> {
    const route = `${this.basePath}/minor-minerals-breakdown`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  /**
   * POST /api/mimas/dashboard/production-summary
   * Request Payload: { financialYear: "2025-2026", districtid: 0 }
   */
  getProductionSummary(financialYear?: string, districtId?: string | number): Observable<any> {
    const route = `${this.basePath}/production-summary`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  /**
   * POST /api/mimas/dashboard/leases-summary
   * Request Payload: { financialYear: "2025-2026", districtid: 0 }
   */
  getLeasesSummary(financialYear?: string, districtId?: string | number): Observable<any> {
    const route = `${this.basePath}/leases-summary`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  /**
   * POST /api/mimas/dashboard/weighbridge-integration
   * Request Payload: { financialYear: "2025-2026", districtid: 0 }
   */
  getWeighbridgeIntegration(financialYear?: string, districtId?: string | number): Observable<any> {
    const route = `${this.basePath}/weighbridge-integration`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  /**
   * 5. POST /api/mimas/dashboard/district-performance
   * District table listings and SVG map metrics for all 38 districts
   */
  getDistrictPerformance(financialYear?: string, districtId?: string | number, sortBy?: string, order?: string): Observable<any> {
    const route = `${this.basePath}/district-performance`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload: any = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    if (sortBy) payload.sortBy = sortBy;
    if (order) payload.order = order;
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  /**
   * POST /api/mimas/dashboard/districtwisedata
   * District Selector Map & District-wise Analytics
   */
  getDistrictWiseData(financialYear?: string, districtId?: string | number): Observable<any> {
    const route = `${this.basePath}/districtwisedata`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  /**
   * 6. POST /api/mimas/dashboard/mines-ranking
   * Top 5 and Bottom 5 (Least) revenue-generating mines
   */
  getMinesRanking(financialYear?: string, districtId?: string | number, limit?: number): Observable<any> {
    const route = `${this.basePath}/mines-ranking`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload: any = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    if (limit !== undefined) {
      payload.limit = limit;
    }
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  /**
   * 7. GET /api/mimas/dashboard/role-view/:roleId
   * Operational dashboard data tailored to the logged-in role
   * (minister, acs, director, district, lease, weighbridge, enforcement, vts, lessee)
   */
  getRoleView(roleId: string): Observable<any> {
    const route = `${this.basePath}/role-view/${encodeURIComponent(roleId)}`;
    return this.dataService.getRecord(route);
  }

  /**
   * 8. Drilldown API
   * POST /api/mimas/dashboard/drilldown/total (Production Total Split-up)
   * GET /api/mimas/dashboard/drilldown/:type (Other drilldowns)
   */
  getDrilldown(type: string, params?: { [key: string]: any }): Observable<any> {
    const route = `${this.basePath}/drilldown/${encodeURIComponent(type)}`;

    if (type === 'gps') {
      const numId = (params?.districtid !== undefined && params?.districtid !== null && !isNaN(Number(params.districtid)))
        ? Number(params.districtid)
        : (params?.districtId !== undefined && !isNaN(Number(params.districtId)) ? Number(params.districtId) : 0);
      const page = params?.page ? Number(params.page) : 1;
      const limit = params?.limit !== undefined ? Number(params.limit) : 10;
      const start = params?.start !== undefined ? Number(params.start) : (page - 1) * limit;
      const category = params?.category || 'total';
      const search = (params?.search || '').trim();

      const payload: any = {
        districtid: numId,
        category,
        page,
        limit,
        start,
        search
      };

      const qParams = new URLSearchParams();
      qParams.set('page', String(page));
      qParams.set('limit', String(limit));
      qParams.set('start', String(start));
      qParams.set('category', String(category));
      qParams.set('districtid', String(numId));
      if (search) qParams.set('search', search);

      const routeWithQuery = `${route}?${qParams.toString()}`;
      return this.dataService.post(routeWithQuery, payload);
    }

    if (type === 'total' || type === 'major' || type === 'minor' || type === 'revenue' || type === 'leases' || type === 'permits' || type === 'production' || type === 'weighbridge' || type === 'active-leases') {
      const numId = (params?.districtid !== undefined && params?.districtid !== null && !isNaN(Number(params.districtid)))
        ? Number(params.districtid)
        : 0;
      const payload: any = {
        districtid: numId,
        financialYear: params?.financialYear || financialYearFor()
      };
      if (params?.mineralCategory) {
        payload.mineralCategory = params.mineralCategory;
      }
      if (params?.category) {
        payload.category = params.category;
      }
      return this.cached(route, payload, () => this.dataService.post(route, payload));
    }
    if (params && Object.keys(params).length > 0) {
      return this.dataService.getRecordWithParams(route, params);
    }
    return this.dataService.getRecord(route);
  }

  /**
   * POST /api/mimas/dashboard/drilldown/weighbridge
   * Request Payload: { financialYear: "2025-2026", districtid: 0, mineralCategory: "major" }
   */
  getWeighbridgeDrilldown(financialYear?: string, districtId?: string | number, mineralCategory?: string): Observable<any> {
    const route = `${this.basePath}/drilldown/weighbridge`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const cat = mineralCategory || 'major';
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId,
      mineralCategory: cat,
      category: cat
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  postDrilldown(type: string, payload?: any): Observable<any> {
    const route = `${this.basePath}/drilldown/${encodeURIComponent(type)}`;
    const numId = (payload?.districtid !== undefined && payload?.districtid !== null && !isNaN(Number(payload.districtid)))
      ? Number(payload.districtid)
      : 0;
    const body = {
      financialYear: payload?.financialYear || financialYearFor(),
      districtid: numId
    };
    return this.dataService.post(route, body);
  }

  /**
   * POST /api/mimas/dashboard/district-selector-production
   * Production-specific metrics for District Selector Map
   * Request Payload: { financialYear: "2025-2026", districtid: 0 }
   */
  getDistrictSelectorProduction(financialYear?: string, districtId?: string | number): Observable<any> {
    const route = `${this.basePath}/district-selector-production`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  /**
   * POST /api/mimas/dashboard/district-selector-revenue
   * Revenue-specific metrics for District Selector Map
   * Request Payload: { financialYear: "2025-2026", districtid: 0 }
   */
  getDistrictSelectorRevenue(financialYear?: string, districtId?: string | number): Observable<any> {
    const route = `${this.basePath}/district-selector-revenue`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  /**
   * POST /api/mimas/dashboard/district-selector-enforcement
   * Enforcement-specific metrics for District Selector Map (Illicit Mining, Vehicle Seizures, Lease Inspections)
   * Request Payload: { financialYear: "2025-2026", districtid: 0 }
   */
  getDistrictSelectorEnforcement(financialYear?: string, districtId?: string | number): Observable<any> {
    const route = `${this.basePath}/district-selector-enforcement`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }
    /**
   * POST /api/mimas/dashboard/lease-rule-application
   * Unified single API returning both Rule Summary counts (Bar Chart) and Application Drilldown items
   * Request Payload: { financialYear: "2025-2026", districtid: 0 }
   */
  getLeaseRuleApplication(financialYear?: string, districtId?: string | number): Observable<any> {
    const route = `${this.basePath}/lease-rule-application`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  /**
   * POST /api/mimas/dashboard/regulatory-status
   * Request Payload: { financialYear: "2026-2027", districtid: 0, category?: "kilns" | "chamber" | "country" }
   * With a category the response also carries the brick kiln applications list.
   * Summary shape used by the overview section:
   * { crushers: { crusherWithStockyard: { total, working, expired }, stockyard: { total, working, expired } },
   *   brickKilns: { chamber: { total, registered, pending }, country: { total, registered, pending } } }
   */
  getRegulatoryStatus(financialYear?: string, districtId?: string | number, category?: string): Observable<any> {
    const route = `${this.basePath}/regulatory-status`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload: any = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    if (category) {
      payload.category = category;
    }
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  /**
   * POST /api/mimas/dashboard/illicit-mining
   * Unified single API returning both Summary KPI counts and Drilldown Month-wise / Application items
   * Request Payload: { financialYear: "2025-2026", districtid: 0 }
   */
  getIllicitMining(financialYear?: string, districtId?: string | number): Observable<any> {
    const route = `${this.basePath}/illicit-mining`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  /**
   * POST /api/mimas/dashboard/vehicle-seizures
   * Unified single API returning both Vehicle Seizures Summary KPIs and Drilldown Month-wise / Seizure records
   * Request Payload: { financialYear: "2025-2026", districtid: 0 }
   */
  getVehicleSeizures(financialYear?: string, districtId?: string | number): Observable<any> {
    const route = `${this.basePath}/vehicle-seizures`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  /**
   * POST /api/mimas/dashboard/lease-inspection
   * Unified single API returning both Lease Inspection Summary KPIs and Drilldown Month-wise / Inspection records
   * Request Payload: { financialYear: "2025-2026", districtid: 0 }
   */
  getLeaseInspection(financialYear?: string, districtId?: string | number): Observable<any> {
    const route = `${this.basePath}/lease-inspection`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }

  /**
   * POST /api/mimas/dashboard/lease-compliance-governance
   * Action Pipeline, Breakdown by Violation Type, Inspection Methods, Departments & Records
   * Request Payload: { financialYear: "2025-2026", districtid: 0 }
   */
  getLeaseComplianceGovernance(financialYear?: string, districtId?: string | number): Observable<any> {
    const route = `${this.basePath}/lease-compliance-governance`;
    const numId = (districtId !== undefined && districtId !== null && !isNaN(Number(districtId))) ? Number(districtId) : 0;
    const payload = {
      financialYear: financialYear || financialYearFor(),
      districtid: numId
    };
    return this.cached(route, payload, () => this.dataService.post(route, payload));
  }
}

